import { useMemo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { ChildTeamsTable } from "@/components/teams/child-teams-table";
import { useEpicsPaged } from "@/hooks/queries/use-epics";
import {
  useTeam,
  useTeamChildren,
  useTeamDescendants,
} from "@/hooks/queries/use-teams";
import type { Epic } from "@/lib/api/types";

interface TeamOverviewTabProps {
  teamId: string;
}

function TeamStatCard({
  label,
  value,
  description,
}: {
  label: string;
  value: ReactNode;
  description: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-3">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <div className="mt-2 text-2xl font-semibold text-foreground">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{description}</div>
    </div>
  );
}

function StatCardsSkeleton() {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="h-[108px] animate-pulse rounded-2xl bg-muted/60" />
      ))}
    </div>
  );
}

function OverviewSectionSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
      <div className="h-5 w-40 animate-pulse rounded bg-muted/60" />
      <div className="mt-2 h-4 w-72 animate-pulse rounded bg-muted/60" />
      <div className="mt-6 space-y-3">
        {Array.from({ length: rows }).map((_, index) => (
          <div key={index} className="h-14 animate-pulse rounded-2xl bg-muted/60" />
        ))}
      </div>
    </div>
  );
}

function OverviewSectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {action}
    </div>
  );
}

function getEpicProgress(epic: Epic) {
  const totalFeatures = epic._count?.features ?? 0;
  const completedFeatures = epic.completedFeatureCount ?? 0;

  if (totalFeatures === 0) {
    return 0;
  }

  return Math.round((completedFeatures / totalFeatures) * 100);
}

function getEpicFeatureSummary(epic: Epic) {
  const totalFeatures = epic._count?.features ?? 0;
  const completedFeatures = epic.completedFeatureCount ?? 0;

  if (totalFeatures === 0) {
    return "No features created yet";
  }

  return `${completedFeatures} of ${totalFeatures} feature${totalFeatures === 1 ? "" : "s"} complete`;
}

export function TeamOverviewTab({ teamId }: TeamOverviewTabProps) {
  const { data: team, isLoading: isTeamLoading } = useTeam(teamId);
  const { data: childTeams, isLoading: isChildrenLoading } = useTeamChildren(teamId);
  const { data: descendants, isLoading: isDescendantsLoading } = useTeamDescendants(teamId);
  const { data: activeEpicsResponse, isLoading: isActiveEpicsLoading } = useEpicsPaged({
    teamId,
    state: "active",
    limit: 5,
    orderBy: "updatedAt",
    orderDirection: "desc",
  });

  const directChildren = childTeams ?? [];
  const allDescendants = descendants ?? [];
  const activeEpics = activeEpicsResponse?.data ?? [];
  const activeEpicCount = activeEpicsResponse?.meta.totalCount ?? 0;

  const descendantEpicOwnerCount = useMemo(
    () => allDescendants.filter((descendant) => descendant.canOwnEpics).length,
    [allDescendants]
  );

  if (isTeamLoading || !team) {
    return (
      <div className="space-y-6">
        <StatCardsSkeleton />
        <OverviewSectionSkeleton />
        <OverviewSectionSkeleton rows={3} />
        <OverviewSectionSkeleton rows={2} />
        <OverviewSectionSkeleton rows={3} />
      </div>
    );
  }

  if (!team.canOwnEpics) {
    return (
      <div className="space-y-6">
        <div className="grid gap-3 md:grid-cols-3">
          <TeamStatCard
            label="Direct children"
            value={
              isChildrenLoading ? (
                <div className="h-8 w-16 animate-pulse rounded bg-muted/60" />
              ) : (
                directChildren.length
              )
            }
            description="Immediate teams nested beneath this one"
          />
          <TeamStatCard
            label="Descendants"
            value={
              isDescendantsLoading ? (
                <div className="h-8 w-16 animate-pulse rounded bg-muted/60" />
              ) : (
                allDescendants.length
              )
            }
            description="Total teams below this point in the hierarchy"
          />
          <TeamStatCard
            label="Epic owner teams"
            value={
              isDescendantsLoading ? (
                <div className="h-8 w-16 animate-pulse rounded bg-muted/60" />
              ) : (
                descendantEpicOwnerCount
              )
            }
            description="Disciplines available lower in the hierarchy"
          />
        </div>

        <div className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Child teams</h2>
            <p className="text-sm text-muted-foreground">
              Browse the immediate teams nested beneath {team.name} and drill deeper into the
              hierarchy.
            </p>
          </div>
          <ChildTeamsTable teams={directChildren} isLoading={isChildrenLoading} />
        </div>

      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <OverviewSectionHeader
          title="Epic progress"
          description={`Active epics for ${team.name}, ordered by recent activity and capped to the latest five.`}
          action={
            <Link
              className="inline-flex items-center gap-1 text-sm text-foreground hover:text-primary"
              to={`/epics?teamId=${team.id}`}
            >
              View all
              <ExternalLink className="h-4 w-4" />
            </Link>
          }
        />

        {isActiveEpicsLoading ? (
          <OverviewSectionSkeleton rows={5} />
        ) : activeEpics.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/[0.08] px-4 py-10 text-center text-sm text-muted-foreground">
            No active epics yet. This discipline is ready for its first epic.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-4">
            <div className="space-y-4">
              {activeEpics.map((epic) => {
                const totalFeatures = epic._count?.features ?? 0;
                const completedFeatures = epic.completedFeatureCount ?? 0;
                const progress = getEpicProgress(epic);

                return (
                  <div
                    key={epic.id}
                    className="border-b border-white/[0.05] pb-4 last:border-b-0 last:pb-0"
                  >
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/epics/${epic.id}`}
                            className="truncate font-medium text-foreground transition-colors hover:text-primary"
                          >
                            {epic.identifier ? `${epic.identifier} — ${epic.name}` : epic.name}
                          </Link>
                          {epic.currentFeatureIdentifier ? (
                            <span className="rounded-full border border-white/[0.08] bg-white/[0.04] px-2 py-0.5 text-[11px] text-muted-foreground">
                              Current {epic.currentFeatureIdentifier}
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {getEpicFeatureSummary(epic)}
                        </p>
                      </div>

                      <div className="w-full md:max-w-xs">
                        <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                          <span>
                            {totalFeatures > 0
                              ? `${completedFeatures}/${totalFeatures} features`
                              : "Awaiting first feature"}
                          </span>
                          <span className="font-medium text-foreground">{progress}%</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/[0.08]">
                          <div
                            className="h-full rounded-full bg-emerald-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {activeEpicCount > activeEpics.length ? (
              <div className="mt-4 border-t border-white/[0.08] pt-4 text-sm text-muted-foreground">
                Showing {activeEpics.length} of {activeEpicCount} active epics.
              </div>
            ) : null}
          </div>
        )}
      </div>

    </div>
  );
}
